import React, { useState, useEffect } from "react";
import { TextField, Button, IconButton, Typography, Box, Tooltip, CircularProgress, Paper, Chip, Stack, LinearProgress } from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import api, { baseURL } from "../../conf/api";
import CachedIcon from "@mui/icons-material/Cached";
import FolderOpenIcon from "@mui/icons-material/FolderOpen";
import FolderIcon from "@mui/icons-material/Folder";
import AddIcon from "@mui/icons-material/Add";
import { toast } from "react-toastify";

const DropboxPaths = () => {
    const [paths, setPaths] = useState([]);
    const [newPath, setNewPath] = useState("");
    const [error, setError] = useState("");
    const [loading, setLoading] = useState(false);
    const [isDropBoxConnected, setDropboxStatus] = useState(undefined);

    useEffect(() => {
        fetchPaths();
    }, []);

    useEffect(() => {
        const CheckDropBoxSatatus = async () => {
            setLoading(true);
            try {
                const res = await api.get("/auth/dropbox/authenticated");

                if (res.status === 200) {
                    setDropboxStatus(true);
                }
            } catch (err) {
                setDropboxStatus(false);
                // GetDropboxURL();
            } finally {
                setLoading(false);
            }
        };
        CheckDropBoxSatatus();

        // Set up an interval to run the function every 2 minutes (120000 milliseconds)
        const intervalId = setInterval(CheckDropBoxSatatus, 120000);

        // Clean up the interval when the component is unmounted
        return () => clearInterval(intervalId);
    }, []);

    const fetchPaths = async () => {
        try {
            const response = await api.get("/auth/dropbox/paths");
            setPaths(response.data?.folders);
        } catch (error) {
            console.error("Error fetching paths", error);
        }
    };

    const handleAddPath = async () => {
        if (!newPath.trim()) return;
        setLoading(true);
        try {
            const response = await api.post("/auth/dropbox/check-folder/", { path: newPath });
            if (response.data.exists) {
                const add_res = await api.post("/auth/dropbox/folder/", { path: newPath });
                if (add_res.status === 201) {
                    setPaths([...paths, newPath]);
                    setNewPath("");
                    setError("");
                }
                else {
                    toast.error(add_res.data.message)
                    setError(add_res.data.message);
                }
            } else {
                setError("Path does not exist in Dropbox");
            }
        } catch (error) {
            const resp = error.response;
            if ((resp?.status === 404 || resp?.status === 400) && resp?.data?.error) {
                toast.error(resp?.data?.error);
            }
            console.error("Error checking/adding path", resp);
            setError("Failed to verify or add path");
        }
        finally {
            setLoading(false);
        }
    };

    const handleRemovePath = async (path) => {
        try {
            setLoading(true);
            await api.delete(`/auth/dropbox/folder`, { data: { path } });
            setPaths(paths.filter((p) => p !== path));
        } catch (error) {
            const resp = error.response;
            if (resp?.data?.error) {
                toast.warning(resp.data.error);
            }
            console.error("Error removing path", resp?.data);
        } finally {
            setLoading(false);
        }
    };


    return (
        <Box sx={{ p: { xs: 2, md: 3 }, maxWidth: 1400, mx: "auto", width: "100%" }}>
            <Stack direction={{ xs: "column", sm: "row" }} justifyContent="space-between" alignItems={{ xs: "flex-start", sm: "center" }} gap={2} mb={3}>
                <Box>
                    <Typography variant="h4" component="h1" fontWeight={600} sx={{ fontSize: { xs: "1.5rem", md: "2.125rem" } }}>
                        Manage Dropbox Paths
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        Folders added here are monitored for new videos.
                    </Typography>
                </Box>
                <Stack direction="row" alignItems="center" gap={1} flexWrap="wrap">
                    {isDropBoxConnected !== undefined && (
                        <Tooltip title={isDropBoxConnected ? "" : "Click to authenticate dropbox"}>
                            <Chip
                                label={`Storage ${isDropBoxConnected ? "connected" : "disconnected"}`}
                                color={isDropBoxConnected ? "success" : "error"}
                                variant="outlined"
                                {...(!isDropBoxConnected && { component: "a", href: `${baseURL}/auth/dropbox/access_token/`, target: "_blank", rel: "noreferrer", clickable: true })}
                                sx={{ height: 44, fontSize: "0.9rem", px: 0.5 }}
                            />
                        </Tooltip>
                    )}
                    <Tooltip title="Refresh paths">
                        <IconButton
                            aria-label="Refresh paths"
                            onClick={() => { fetchPaths(); setNewPath(""); setError(""); }}
                            sx={{ width: 44, height: 44, "&:focus-visible": { outline: "2px solid", outlineColor: "primary.main" } }}
                        >
                            <CachedIcon />
                        </IconButton>
                    </Tooltip>
                </Stack>
            </Stack>

            <Paper variant="outlined" sx={{ borderRadius: 2, p: { xs: 2, md: 3 }, mb: 3 }}>
                <Typography variant="h6" component="h2" fontWeight={600} mb={2}>Add a folder</Typography>
                <Stack direction={{ xs: "column", sm: "row" }} gap={2} alignItems={{ sm: "flex-start" }}>
                    <TextField
                        label="Dropbox Folder Path"
                        variant="outlined"
                        fullWidth
                        value={newPath}
                        onChange={(e) => setNewPath(e.target.value)}
                        onKeyDown={(e) => { if (e.key === "Enter" && isDropBoxConnected && !loading) handleAddPath(); }}
                        error={Boolean(error)}
                        helperText={error || "Example: /Videos/Creators"}
                        disabled={loading}
                        InputProps={{ startAdornment: loading ? <CircularProgress size={20} sx={{ mr: 1 }} /> : null }}
                    />
                    <Button
                        variant="contained"
                        color="primary"
                        startIcon={<AddIcon />}
                        disabled={!isDropBoxConnected || loading}
                        onClick={handleAddPath}
                        sx={{ minHeight: 56, minWidth: 140, flexShrink: 0, width: { xs: "100%", sm: "auto" } }}
                    >
                        Add Path
                    </Button>
                </Stack>
                {isDropBoxConnected === false && (
                    <Typography variant="body2" color="error" mt={1}>
                        Dropbox is disconnected. Authenticate it to add paths.
                    </Typography>
                )}
            </Paper>

            <Paper variant="outlined" sx={{ borderRadius: 2, p: { xs: 2, md: 3 } }}>
                <Typography variant="h6" component="h2" fontWeight={600} mb={1}>
                    Monitored folders {paths?.length ? `(${paths.length})` : ""}
                </Typography>
                {loading && <LinearProgress sx={{ mb: 1 }} aria-label="Loading" />}
                {!paths?.length ? (
                    <Box sx={{ textAlign: "center", py: 6, color: "text.secondary" }}>
                        <FolderOpenIcon sx={{ fontSize: 48, mb: 1 }} aria-hidden="true" />
                        <Typography>No folders added yet.</Typography>
                        <Typography variant="body2">Enter a Dropbox path above and choose Add Path.</Typography>
                    </Box>
                ) : (
                    <Box component="ul" sx={{ listStyle: "none", m: 0, p: 0 }}>
                        {paths.map((path, index) => (
                            <Box
                                component="li"
                                key={index}
                                sx={{
                                    display: "flex", alignItems: "center", gap: 1.5, minHeight: 56, py: 0.5, px: 1,
                                    borderTop: index ? 1 : 0, borderColor: "divider",
                                    transition: "background-color 200ms",
                                    "&:hover": { bgcolor: "action.hover" },
                                    "@media (prefers-reduced-motion: reduce)": { transition: "none" },
                                }}
                            >
                                <FolderIcon color="primary" aria-hidden="true" />
                                <Typography sx={{ flexGrow: 1, minWidth: 0, wordBreak: "break-all" }}>{path}</Typography>
                                <Tooltip title="Remove path">
                                    <span>
                                        <IconButton
                                            aria-label={`Remove ${path}`}
                                            onClick={() => handleRemovePath(path)}
                                            disabled={loading}
                                            sx={{ width: 44, height: 44, "&:focus-visible": { outline: "2px solid", outlineColor: "primary.main" } }}
                                        >
                                            <DeleteIcon />
                                        </IconButton>
                                    </span>
                                </Tooltip>
                            </Box>
                        ))}
                    </Box>
                )}
            </Paper>
        </Box>
    );
};

export default DropboxPaths;
